import { useEffect, useState } from "react";
import { useModalTransition } from "@/hooks/useModalTransition";
import {
  ComposeInlineReplySlot,
  COMPOSE_INLINE_REPLY_TRANSITION_MS,
} from "@/features/compose/ComposeInlineReply";
import { MessageBodyView } from "./MessageBodyView";
import { MessageBodyPreloader } from "./MessageBodyPreloader";
import { MessageReaderHeader } from "./MessageReaderHeader";
import { MessageAttachments } from "./MessageAttachments";
import { AttachmentPreview } from "./AttachmentPreview";
import { isFetchedBodyComplete } from "@/lib/mail/messageBodyCacheStatus";
import { subscribeMessageBodyCache } from "@/lib/cache/messageBodyCache";
import { firstPreviewableAttachment } from "@/lib/mail/attachmentPreview";
import type { OutboxEnqueuePayload } from "@/lib/outbox/outboxQueue";
import type { ComposeDraft } from "@/lib/types/compose";
import type { MailAttachment, MailMessageDetail, MailAccountSummary } from "@/lib/types/mail";
import type { StoredComposeSession } from "@/lib/compose/composeSessionsStore";
import { isInlineComposeSession } from "@/lib/compose/composePlacement";

type Props = {
  message: MailMessageDetail | null;
  loading: boolean;
  error: string | null;
  selectedUid: number | null;
  accountId?: number;
  folder?: string;
  flagged?: boolean;
  favorite?: boolean;
  seen?: boolean;
  onAction?: (actionId: string) => void;
  onRetry?: () => void;
  /** Sélection multiple active — grise répondre / transférer. */
  bulkSelectionActive?: boolean;
  /** Réponse inline (desktop) au-dessus du message original. */
  inlineComposeSession?: StoredComposeSession | null;
  composeAccounts?: MailAccountSummary[];
  composeAccountEmail?: string;
  composeSettingsVersion?: number;
  onUpdateComposeDraft?: (sessionId: string, patch: Partial<ComposeDraft>) => void;
  onSaveComposeDraft?: (sessionId: string, draft: ComposeDraft) => void;
  onDiscardComposeDraft?: (sessionId: string, draft: ComposeDraft) => void;
  onCloseComposeSession?: (sessionId: string) => void;
  onComposeEnqueue?: (sessionId: string, payload: OutboxEnqueuePayload) => void;
  onScheduleCompose?: (sessionId: string, payload: OutboxEnqueuePayload, sendAt: string) => void;
  composeHandoffSessionId?: string | null;
};

type BodyPanelProps = {
  message: MailMessageDetail;
  loading: boolean;
  error: string | null;
  hasBody: boolean;
  accountId?: number;
  folder: string;
  onRetry?: () => void;
  bodyLayout?: "pane" | "flow";
};

function MessageReaderBodyPanel({
  message,
  loading,
  error,
  hasBody,
  accountId,
  folder,
  onRetry,
  bodyLayout = "pane",
}: BodyPanelProps) {
  const flowLayout = bodyLayout === "flow";
  // Filet UI : headers sans corps et sans erreur = encore en cours (shimmer),
  // pas « Contenu indisponible » (aligné mobile `bodyPending`).
  const bodyPending = !hasBody && !error && !loading;
  const showSkeleton = !hasBody && (loading || bodyPending);

  return (
    <div className={flowLayout ? "mail-reader-body-below-compose relative w-full" : "relative h-full min-h-0 w-full"}>
      {showSkeleton ? (
        <MessageBodyPreloader
          overlay={!flowLayout}
          quiet
          label="Ouverture…"
        />
      ) : null}
      {hasBody ? (
        <MessageBodyView
          html={message.bodyHtml}
          text={message.bodyText}
          from={message.from}
          layout={bodyLayout}
          messageKey={
            accountId != null ? { accountId, folder, uid: message.uid } : undefined
          }
        />
      ) : showSkeleton ? null : error ? (
        <div
          className={
            flowLayout
              ? "flex min-h-[12rem] items-center justify-center bg-[var(--mail-surface)] p-4"
              : "absolute inset-0 flex items-center justify-center bg-[var(--mail-surface)] p-4"
          }
        >
          <div className="max-w-sm space-y-3 text-center">
            <p className="mail-alert-error text-sm" role="alert">
              {error}
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                Actualiser le message
              </button>
            ) : null}
          </div>
        </div>
      ) : message.hasAttachments ? (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-[var(--mail-muted)]">
          Ce message ne contient pas de texte — consultez les pièces jointes.
        </div>
      ) : (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-[var(--mail-muted)]">
          Contenu indisponible. Réessayez ou actualisez le message.
        </div>
      )}
    </div>
  );
}

export function MessageReader({
  message,
  loading,
  error,
  selectedUid,
  accountId,
  folder = "INBOX",
  flagged = false,
  favorite = false,
  seen,
  onAction,
  onRetry,
  bulkSelectionActive = false,
  inlineComposeSession = null,
  composeAccounts = [],
  composeAccountEmail,
  composeSettingsVersion = 0,
  onUpdateComposeDraft,
  onSaveComposeDraft,
  onDiscardComposeDraft,
  onCloseComposeSession,
  onComposeEnqueue,
  onScheduleCompose,
  composeHandoffSessionId = null,
}: Props) {
  const [previewAttachment, setPreviewAttachment] = useState<MailAttachment | null>(null);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [bodyCacheVersion, setBodyCacheVersion] = useState(0);

  useEffect(() => {
    setPreviewAttachment(null);
    setPreviewDismissed(false);
  }, [selectedUid, message?.uid]);

  useEffect(() => {
    if (previewDismissed || !message?.attachments?.length || accountId == null) return;
    const attachments = message.attachments;
    setPreviewAttachment((current) => current ?? firstPreviewableAttachment(attachments));
  }, [message?.attachments, message?.uid, accountId, previewDismissed]);

  useEffect(() => subscribeMessageBodyCache(() => setBodyCacheVersion((v) => v + 1)), []);
  void bodyCacheVersion;

  const showInlineCompose =
    selectedUid != null &&
    inlineComposeSession != null &&
    isInlineComposeSession(inlineComposeSession) &&
    onUpdateComposeDraft != null &&
    onSaveComposeDraft != null &&
    onDiscardComposeDraft != null &&
    onCloseComposeSession != null &&
    onComposeEnqueue != null;

  const { mounted: inlineComposeMounted } = useModalTransition(
    showInlineCompose,
    COMPOSE_INLINE_REPLY_TRANSITION_MS
  );
  const inlineComposeActive = showInlineCompose || inlineComposeMounted;

  const showAttachmentPreview =
    previewAttachment != null && accountId != null && message != null;
  const hasBody = message != null && isFetchedBodyComplete(message);

  if (!selectedUid) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 p-6 text-center text-[var(--mail-muted)]">
        <p className="text-sm font-medium">Sélectionnez un message</p>
        <p className="max-w-xs text-xs">Choisissez un e-mail dans la liste pour l’afficher ici.</p>
      </div>
    );
  }

  if (error && !message && !loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-sm space-y-3 text-center">
            <p className="mail-alert-error text-sm" role="alert">
              {error}
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                Actualiser le message
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  function handlePreviewToggle(file: MailAttachment | null) {
    if (file == null) {
      setPreviewDismissed(true);
    }
    setPreviewAttachment(file);
  }

  function handleClosePreview() {
    setPreviewDismissed(true);
    setPreviewAttachment(null);
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      {message ? (
        <>
          <MessageReaderHeader
            subject={message.subject}
            from={message.from}
            to={message.to}
            cc={message.cc}
            date={message.date}
            seen={seen ?? message.seen ?? true}
            flagged={flagged}
            favorite={favorite}
            onAction={onAction}
            bulkSelectionActive={bulkSelectionActive}
          />
          {message.attachments && message.attachments.length > 0 && accountId ? (
            <MessageAttachments
              accountId={accountId}
              folder={folder}
              messageUid={message.uid}
              attachments={message.attachments}
              previewPart={previewAttachment?.part ?? null}
              onPreview={handlePreviewToggle}
            />
          ) : null}
        </>
      ) : loading ? (
        <div className="shrink-0 space-y-1.5 border-b border-[var(--mail-border-soft)] px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-[var(--mail-surface-hover)]" />
            <div className="h-3 w-32 animate-pulse rounded bg-[var(--mail-surface-hover)]" />
          </div>
          <div className="h-4 w-[min(100%,16rem)] animate-pulse rounded bg-[var(--mail-surface-hover)]" />
        </div>
      ) : null}

      <div
        className={
          inlineComposeMounted
            ? "mail-reader-scroll relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-[var(--mail-surface)]"
            : "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--mail-surface)]"
        }
      >
        {inlineComposeActive &&
        onUpdateComposeDraft &&
        onSaveComposeDraft &&
        onDiscardComposeDraft &&
        onCloseComposeSession &&
        onComposeEnqueue ? (
          <ComposeInlineReplySlot
            open={showInlineCompose}
            session={inlineComposeSession}
            accounts={composeAccounts}
            accountEmail={composeAccountEmail}
            settingsVersion={composeSettingsVersion}
            onUpdateDraft={onUpdateComposeDraft}
            onSaveDraft={onSaveComposeDraft}
            onDiscardDraft={onDiscardComposeDraft}
            onCloseSession={onCloseComposeSession}
            onEnqueue={onComposeEnqueue}
            onScheduleSend={onScheduleCompose}
            sending={
              composeHandoffSessionId != null &&
              composeHandoffSessionId === inlineComposeSession?.id
            }
          />
        ) : null}
        {showAttachmentPreview && message ? (
          <div className={inlineComposeActive ? "mail-reader-split mail-reader-split--inline" : "mail-reader-split"}>
            <div className="mail-reader-split__body">
              <MessageReaderBodyPanel
                message={message}
                loading={loading}
                error={error}
                hasBody={hasBody}
                accountId={accountId}
                folder={folder}
                onRetry={onRetry}
                bodyLayout={inlineComposeActive ? "flow" : "pane"}
              />
            </div>
            <div className="mail-reader-split__preview">
              <AttachmentPreview
                variant="split"
                accountId={accountId}
                folder={folder}
                messageUid={message.uid}
                attachment={previewAttachment}
                onClose={handleClosePreview}
              />
            </div>
          </div>
        ) : message ? (
          <MessageReaderBodyPanel
            message={message}
            loading={loading}
            error={error}
            hasBody={hasBody}
            accountId={accountId}
            folder={folder}
            onRetry={onRetry}
            bodyLayout={inlineComposeActive ? "flow" : "pane"}
          />
        ) : null}
      </div>
    </div>
  );
}
